import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { DoorOpen, KeyRound, RefreshCw, PlugZap } from "lucide-react";
import { toast } from "sonner";

const ADAPTERS = [
  { value: "http", label: "HTTP direct (Shelly / Sonoff / custom)" },
  { value: "gateway", label: "Gateway poller (on-prem device)" },
];

export function DoorControllerCard({ buildingId, buildingLabel }) {
  const [ctrl, setCtrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adapter, setAdapter] = useState("http");
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState("POST");
  const [pulseMs, setPulseMs] = useState(3000);
  const [authToken, setAuthToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [regenKey, setRegenKey] = useState(null);

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/buildings/${buildingId}/door-controller`);
      setCtrl(data);
      if (data.configured) {
        setAdapter(data.adapter_type);
        const c = data.config || {};
        setUrl(c.url || "");
        setMethod(c.method || "POST");
        setPulseMs(c.pulse_ms || 3000);
        setAuthToken("");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load controller");
    } finally {
      setLoading(false);
    }
  }, [buildingId]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const body = { adapter_type: adapter, name: buildingLabel };
      if (adapter === "http") {
        body.config = {
          url,
          method,
          pulse_ms: Number(pulseMs) || 3000,
          ...(authToken ? { auth_token: authToken } : {}),
        };
      }
      await api.put(`/buildings/${buildingId}/door-controller`, body);
      toast.success("Controller saved");
      setRegenKey(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/buildings/${buildingId}/door-controller/test`);
      setTestResult(data.delivery);
      const s = data.delivery?.status;
      if (s === "sent" || s === "acked") toast.success(`Test OK · ${data.delivery.detail}`);
      else if (s === "pending") toast(`Queued for gateway`);
      else toast.warning(`Test result: ${s}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  }

  async function regenerate() {
    if (adapter !== "gateway") return;
    try {
      const { data } = await api.post(`/buildings/${buildingId}/door-controller/regenerate-key`);
      setRegenKey(data.gateway_key);
      toast.success("New gateway key generated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    }
  }

  return (
    <div className="card-stroke p-6 space-y-4" data-testid="door-controller-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display text-xl flex items-center gap-2">
          <DoorOpen size={16}/> Door controller
        </div>
        {ctrl?.configured && (
          <span
            className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-secondary text-secondary-foreground"
            data-testid="door-adapter-badge"
          >
            {ctrl.adapter_type}
          </span>
        )}
      </div>

      <div>
        <Label className="label-tiny">Adapter</Label>
        <Select value={adapter} onValueChange={setAdapter}>
          <SelectTrigger className="mt-2" data-testid="door-adapter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ADAPTERS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {adapter === "http" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="label-tiny">Controller URL</Label>
            <Input value={url} onChange={(e)=>setUrl(e.target.value)}
                   placeholder="http://192.168.1.42/relay/0?turn=on"
                   className="mt-2 font-mono text-sm" data-testid="door-url"/>
          </div>
          <div>
            <Label className="label-tiny">HTTP method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-2" data-testid="door-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tiny">Pulse (ms)</Label>
            <Input value={pulseMs} onChange={(e)=>setPulseMs(e.target.value)}
                   type="number" min="500" max="15000" className="mt-2 font-mono"
                   data-testid="door-pulse"/>
          </div>
          <div className="md:col-span-2">
            <Label className="label-tiny">HMAC / auth token (never shown after save)</Label>
            <Input value={authToken} onChange={(e)=>setAuthToken(e.target.value)}
                   type="password"
                   placeholder={ctrl?.config?.has_auth_token
                     ? `Current: ${ctrl.config.auth_token_preview}`
                     : "leave blank to derive from JWT_SECRET"}
                   className="mt-2 font-mono" data-testid="door-token"/>
          </div>
        </div>
      )}

      {adapter === "gateway" && (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            The on-prem gateway polls
            <span className="font-mono mx-1">GET /api/gateway/commands?building_id={buildingId}</span>
            with header <span className="font-mono">X-Gateway-Key</span>. Rotate the key to invalidate the previous poller.
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono">
              key: {ctrl?.gateway_key_configured ? "configured" : "not set"}
            </span>
            <Button size="sm" variant="outline" onClick={regenerate} data-testid="door-regen-key">
              <KeyRound size={14} className="mr-1.5"/> Regenerate key
            </Button>
          </div>
          {regenKey && (
            <div className="border border-amber-300/40 bg-amber-500/10 rounded p-3 text-xs font-mono break-all"
                 data-testid="door-regen-plaintext">
              <div className="uppercase font-bold text-amber-500 mb-1 text-[10px]">Save this now — shown once</div>
              {regenKey}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Button onClick={save} disabled={saving || (adapter === "http" && !url)}
                data-testid="door-save">
          {saving ? "Saving…" : "Save controller"}
        </Button>
        <Button variant="outline" onClick={testConnection} disabled={testing || !ctrl?.configured}
                data-testid="door-test">
          <PlugZap size={14} className="mr-1.5"/>{testing ? "Testing…" : "Test connection"}
        </Button>
        <Button size="icon" variant="ghost" onClick={load} disabled={loading}
                data-testid="door-refresh" aria-label="Refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
        </Button>
      </div>

      {(testResult || ctrl?.last_delivery) && (
        <div className="text-xs font-mono border-t border-border pt-3"
             data-testid="door-delivery-status">
          <span className="uppercase tracking-widest text-muted-foreground">Last delivery:</span>{" "}
          <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${
            ["sent","acked"].includes((testResult||ctrl.last_delivery)?.status)
              ? "bg-emerald-500/20 text-emerald-300"
              : ["failed","expired"].includes((testResult||ctrl.last_delivery)?.status)
                ? "bg-red-500/20 text-red-300"
                : "bg-secondary text-secondary-foreground"
          }`}>{(testResult||ctrl.last_delivery)?.status}</span>{" "}
          · {(testResult||ctrl.last_delivery)?.detail || "—"}
        </div>
      )}
    </div>
  );
}
